import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [socialMediaUrl, setSocialMediaUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { register } = useAuth();

  const passwordChecks = [
    { label: "8+ characters", test: (p) => p.length >= 8 },
    { label: "Uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "Lowercase letter", test: (p) => /[a-z]/.test(p) },
    { label: "Number", test: (p) => /\d/.test(p) },
    { label: "Special character", test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(p) },
  ];
  const passedChecks = passwordChecks.filter((c) => c.test(password)).length;
  const strengthPercent = password.length === 0 ? 0 : (passedChecks / passwordChecks.length) * 100;
  const strengthColor = strengthPercent <= 40 ? "bg-red-500" : strengthPercent <= 80 ? "bg-amber-500" : "bg-emerald-500";
  const strengthLabel = strengthPercent <= 40 ? "Weak" : strengthPercent <= 80 ? "Fair" : "Strong";
  const allChecksPassed = passedChecks === passwordChecks.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allChecksPassed) {
      setError("Password does not meet all requirements");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, socialMediaUrl);
      setSuccess(true);
      toast.success("Application submitted!");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 animate-fade-in" data-testid="register-success">
        <Card className="w-full max-w-md border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Application Submitted</h2>
            <p className="text-sm text-muted-foreground mb-6">
              We've sent a verification link to your email. Please verify your email, then wait for admin approval before logging in.
            </p>
            <Link to="/login">
              <Button variant="outline" className="rounded-sm" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 animate-fade-in" data-testid="register-page">
      <Card className="w-full max-w-md border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm">
        <CardHeader className="space-y-1">
          <p className="text-xs font-medium tracking-widest uppercase text-emerald-600 mb-1">Join us</p>
          <CardTitle className="font-heading text-2xl font-bold tracking-tight" data-testid="register-title">
            Apply for an account
          </CardTitle>
          <CardDescription>Your application will be reviewed by our team before approval</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="social-media">YouTube / Social Media Page</Label>
              <Input
                id="social-media"
                type="url"
                placeholder="https://youtube.com/@yourchannel"
                value={socialMediaUrl}
                onChange={(e) => setSocialMediaUrl(e.target.value)}
                data-testid="register-social-media-input"
              />
              <p className="text-xs text-muted-foreground">Link to your YouTube, Instagram, TikTok, or other social page</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="register-password-input"
              />
              {password.length > 0 && (
                <div className="space-y-2" data-testid="password-strength">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${strengthColor}`}
                        style={{ width: `${strengthPercent}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${strengthPercent <= 40 ? "text-red-600" : strengthPercent <= 80 ? "text-amber-600" : "text-emerald-600"}`} data-testid="password-strength-label">
                      {strengthLabel}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                    {passwordChecks.map((check, i) => (
                      <p key={i} className={`text-xs flex items-center gap-1 ${check.test(password) ? "text-emerald-600" : "text-muted-foreground"}`}>
                        <span>{check.test(password) ? "\u2713" : "\u2022"}</span> {check.label}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-all hover:shadow-lg hover:shadow-emerald-600/20" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-foreground hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
